import style from "@/styles/pages/PropertyDetails/ClosingDate.module.css";
import { FormProvider, useForm } from "react-hook-form";
import clsx from "clsx";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { RootState } from "@/store/dtm";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import { selectPropertyInfo } from "@/slices/propertyInfoSlice";
import { setClosingDate } from "@/slices/propertyInfoSlice";
import SlideTransition from "@/styles/animation/SlideTransition";
import { createApiClient } from "@/lib/Requests";
import { ApplicationDto } from "@/lib/DTMWebAPIClient.generated";
import { setApplicationProgress } from "@/slices/applicationSlice";
import { selectPropertyAddress } from "@/slices/propertyAddressSlice";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { addBusinessDays, setClickInsightApplicationProgress, setClickInsightEvents, setClickInsightFormErrors } from "@/lib/util";

export default function ClosingDate() {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [closingDateText, setClosingDateText] = useState(dayjs);
    const [date, setDate] = useState(new Date());
    const [displayError, setDisplayError] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    const application = useSelector(
        (state: RootState) => state.application.application
    );
    const applicant = useSelector(
        (state: RootState) => state.applicant.applicant
    );
    const credential = useSelector(
        (state: RootState) => state.credential
    );
    const referralProducts = useSelector((state: RootState) => state.referralProducts.products);
    const client = createApiClient({ timeout: 60000, token: credential.credential.accessToken });
    const propertyInfo = useSelector(selectPropertyInfo).propertyInfo;
    const propertyAddress = useSelector(selectPropertyAddress).address;

    const methods = useForm({
        defaultValues: {
            closingDate: ""
        },
    });

    useEffect(() => {
        document.title = "Enter Closing Date";
        methods.setValue("closingDate", propertyInfo ? propertyInfo.closingDate : "");
        if (propertyInfo != undefined) {
            let dateComponents = propertyInfo.closingDate.split("/");
            let date = dayjs(dateComponents[2] + "-" + dateComponents[1] + "-" + dateComponents[0]);
            setClosingDateText(date);
        }
        setClickInsightEvents(referralProducts, undefined, undefined, undefined, undefined, application.id, applicant);

        client.apiApplicationsFetch().then((result) => {
            result.propertyInfo = propertyInfo;
            result.application = application;
            client.apiApplicationsSave(new ApplicationDto({
                applicationData: JSON.stringify(result)
            })).catch((error) => {
                console.log("Error saving application info");
            });
        }).catch((error) => {
            console.log("Error fetching application info");
        });
    }, []);

    const onError = (errors: object) => {
        setClickInsightFormErrors(errors);
    }

    const onSubmit = async (data: any) => {
        let dateComponents = data.closingDate.split("/");
        let selectedDate = dayjs(dateComponents[2] + "-" + dateComponents[1] + "-" + dateComponents[0]);
        let maxDate = dayjs('2069-12-31');
        if (data.closingDate == "Invalid Date" || selectedDate > maxDate || selectedDate < addBusinessDays(9) || data.closingDate == "") {
            setDisplayError(true);
            return;
        }

        setDisplayError(false);
        dispatch(setClosingDate(data.closingDate));

        let financingConditionDate = undefined;
        if (propertyInfo.financingConditionDate && propertyInfo.financingConditionDate.trim() != "") {
            let dateComponents = propertyInfo.financingConditionDate.split("/");
            financingConditionDate = dayjs(dateComponents[2] + "-" + dateComponents[1] + "-" + dateComponents[0]);
        }

        dispatch(setApplicationProgress(7));
        setClickInsightApplicationProgress('Your new mortgage - What is the closing date for this property?');
        navigate("/single/personal-info");
    };

    return (
        <>
            <SlideTransition>
                <div className="flex flex-col max-xs:px-3 sm:ml-80 max-md:items-center max-md:mt-20">
                    <div
                        className={style.addHeading}
                        role="region"
                        aria-label="Address Header"
                    >
                        <h1 aria-label="Address Header">What is the closing date for this property?</h1>
                        <p className="sub-heading">This is the date you legally take possession of your property. You'll typically negotiate your closing date with the seller and include it in your property's sales contract.</p>
                        {displayError &&
                            <div className={clsx("flex flex-row pcaautocomplete mt-5", style.errorContainer)}>
                                <img src="assets/error-info-icon.svg" alt="error" />
                                <span className="text-sm ml-2 mt-1">
                                    Please select a date on or after {dayjs(addBusinessDays(10)).format('LL')} (10 business days from today)
                                </span>
                            </div>
                        }
                        <div className={clsx("flex flex-row", displayError ? "" : "mt-5")}>
                            <FormProvider {...methods}>
                                <form
                                    autoComplete="off"
                                    onSubmit={methods.handleSubmit(onSubmit, onError)}
                                >
                                    <div className="flex md:flex-row flex-col w-max">
                                        <div className="mt-5">
                                            <label htmlFor="downPayment">Estimated Closing Date</label>
                                            <div className="flex relative">
                                                <span>
                                                    <img
                                                        src="assets/error-icon.svg"
                                                        className={clsx(
                                                            methods.formState?.errors?.closingDate?.message
                                                                ? ""
                                                                : "hidden",
                                                            "w-4 h-4 absolute top-6  max-xxs:ml-[210px] max-sm:ml-[300px]  xs:max-md:ml-[370px] md:ml-[195px]"
                                                        )}
                                                        alt="error"
                                                        height={5}
                                                        width={5}
                                                    />
                                                </span>
                                                <div>
                                                    <DatePicker
                                                        className={style.datePickerInput}
                                                        format="DD MMM YYYY"
                                                        minDate={addBusinessDays(10)}
                                                        value={closingDateText}
                                                        onChange={(e) => {
                                                            if (e != undefined) {
                                                                methods.setValue("closingDate", e.format('DD/MM/YYYY'));
                                                                setClosingDateText(e);
                                                                setDisplayError(false);
                                                            }
                                                        }}
                                                        sx={{ width: window.innerWidth < 768 ? 350 : "100%" }}
                                                        slotProps={{
                                                            textField: {
                                                                error: displayError,
                                                            },
                                                        }}
                                                        localeText={{
                                                            fieldMonthPlaceholder: (params) => (params.contentType === 'letter' ? 'MMM' : 'MM'),
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                            {methods.formState?.errors?.closingDate?.message && (
                                                <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                                                    {methods.formState?.errors?.closingDate?.message.toString()}
                                                </span>
                                            )}
                                        </div>
                                    </div>

                                    <div className="flex max-sm:hidden">
                                        <div className="mb-5 mr-5">
                                            <button onClick={() => navigate(-1)}
                                                type="button"
                                                className="back mt-5 w-169 sm:w-28 h-11 px-6">
                                                Back
                                            </button>
                                        </div>
                                        <div className="mb-5">
                                            <button
                                                type="submit"
                                                className="primary mt-5 w-full sm:w-28 h-11 px-6"
                                                disabled={isSaving}
                                            >
                                                <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: 8 }} className={clsx(isSaving ? "" : "hidden")} />
                                                Continue
                                            </button>
                                        </div>
                                    </div>
                                    <div className="sm:hidden">
                                        <div className="mb-5">
                                            <button
                                                type="submit"
                                                className="primary mt-5 w-full sm:w-28 h-11 px-6"
                                                disabled={isSaving}
                                            >
                                                <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: 8 }} className={clsx(isSaving ? "" : "hidden")} />
                                                Continue
                                            </button>
                                        </div>
                                        <div className="mb-5">
                                            <button onClick={() => navigate(-1)}
                                                type="button"
                                                className="back mt-5 w-full sm:w-28 h-11 px-6">
                                                Back
                                            </button>
                                        </div>
                                    </div>
                                </form>
                            </FormProvider>
                        </div>
                    </div>
                </div>
            </SlideTransition>
        </>
    );
}